import { CheckCircle, Clock, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

type RedemptionStatus = 'held' | 'consumed' | 'refunded';

const statusConfig: Record<
  RedemptionStatus,
  {
    icon: React.ElementType;
    label: string;
    className: string;
    iconClassName: string;
  }
> = {
  held: {
    icon: Clock,
    label: 'Held',
    className:
      'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800',
    iconClassName: 'text-amber-600 dark:text-amber-400',
  },
  consumed: {
    icon: CheckCircle,
    label: 'Consumed',
    className:
      'bg-green-100 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800',
    iconClassName: 'text-green-600 dark:text-green-400',
  },
  refunded: {
    icon: RotateCcw,
    label: 'Refunded',
    className:
      'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700',
    iconClassName: 'text-slate-500 dark:text-slate-400',
  },
};

interface RedemptionStatusBadgeProps {
  status: RedemptionStatus;
  size?: 'sm' | 'md';
  showIcon?: boolean;
}

export function RedemptionStatusBadge({
  status,
  size = 'md',
  showIcon = true,
}: RedemptionStatusBadgeProps) {
  const config = statusConfig[status];
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs gap-1',
    md: 'px-2.5 py-1 text-sm gap-1.5',
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-3.5 w-3.5',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border font-medium',
        sizeClasses[size],
        config.className
      )}
    >
      {showIcon && <Icon className={cn(iconSizes[size], config.iconClassName)} />}
      {config.label}
    </span>
  );
}
